import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  type IAdminProduct,
  type IAdminWarehouseProductDetail,
  type IAdminWarehouseProductList,
  type IProductDownloaded,
  type IProductDownloadedList,
  type IWarehouseCategory,
  type IWarehouseProductSuggestions,
  type IWarehouseProperty,
} from '@mahawi/eshop-common/src/types';
import { createSlice } from '@reduxjs/toolkit';

import {
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
} from '../async-state';
import { type IDownloaderState } from './types';

export const preloadedState: IDownloaderState = {
  inProcess: false,
  products: undefined,
  product: undefined,
  property: undefined,
  warehouse: {
    categories: undefined,
    categoriesUnpaired: undefined,
    category: undefined,
    products: undefined,
    product: undefined,
    properties: undefined,
    suggestions: undefined,
  },
  newProduct: undefined,
  updatedAt: undefined,
};

const downloaderSlice = createSlice({
  name: 'Downloader',
  initialState: preloadedState,
  reducers: {
    downloaderLoadProducts: {
      reducer(state) {
        state.inProcess = true;
        state.products = undefined;
      },
      prepare(lastDaysUpdatedAt: number) {
        return { payload: { lastDaysUpdatedAt } };
      },
    },
    downloaderLoadProductsRespose: {
      reducer(
        state,
        action: { payload: { products: IProductDownloadedList[] } },
      ) {
        state.inProcess = false;
        state.products = action.payload.products;
        state.updatedAt = Date.now();
      },
      prepare(products: IProductDownloadedList[]) {
        return { payload: { products } };
      },
    },
    downloaderLoadProduct: {
      reducer(state) {
        state.inProcess = true;
        state.product = undefined;
      },
      prepare(productDownloadedUUID: string) {
        return {
          payload: { productDownloaded: { uuid: productDownloadedUUID } },
        };
      },
    },
    downloaderLoadProductResponse: {
      reducer(
        state,
        action: { payload: { product: IProductDownloaded | null } },
      ) {
        state.inProcess = false;
        state.product = action.payload.product;
        state.updatedAt = Date.now();
      },
      prepare(product: IProductDownloaded | null) {
        return { payload: { product } };
      },
    },
    downloaderLoadWarehouseCategories: {
      reducer(state) {
        state.inProcess = true;
        state.warehouse.categories = undefined;
      },
      prepare() {
        return { payload: undefined };
      },
    },
    downloaderLoadWarehouseCategoriesResponse: {
      reducer(
        state,
        action: { payload: { categories: IWarehouseCategory[] } },
      ) {
        state.inProcess = false;
        state.warehouse.categories = action.payload.categories;
        state.updatedAt = Date.now();
      },
      prepare(categories: IWarehouseCategory[]) {
        return { payload: { categories } };
      },
    },
    downloaderLoadWarehouseCategoriesUnpaired: {
      reducer(state) {
        state.inProcess = true;
        state.warehouse.categoriesUnpaired = undefined;
      },
      prepare() {
        return { payload: undefined };
      },
    },
    downloaderLoadWarehouseCategoriesUnpairedResponse: {
      reducer(
        state,
        action: { payload: { categories: IWarehouseCategory[] } },
      ) {
        state.inProcess = false;
        state.warehouse.categoriesUnpaired = action.payload.categories;
        state.updatedAt = Date.now();
      },
      prepare(categories: IWarehouseCategory[]) {
        return { payload: { categories } };
      },
    },
    downloaderLoadWarehouseCategory: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(uuid: string) {
        return { payload: { warehouseCategory: { uuid } } };
      },
    },
    downloaderLoadWarehouseCategoryResponse: {
      reducer(
        state,
        action: { payload: { category: IWarehouseCategory | null } },
      ) {
        state.inProcess = false;
        state.warehouse.category = action.payload.category;
        state.updatedAt = Date.now();
      },
      prepare(category: IWarehouseCategory | null) {
        return { payload: { category } };
      },
    },
    downloaderPairWarehouseCategory: {
      reducer(state) {
        state.inProcess = true;
        state.warehouse.category = undefined;
      },
      prepare(uuid: string, categoryUUID: string) {
        return {
          payload: {
            warehouseCategory: { uuid },
            category: { uuid: categoryUUID },
          },
        };
      },
    },
    downloaderPairWarehouseCategoryResponse: {
      reducer(
        state,
        action: { payload: { category: IWarehouseCategory | null } },
      ) {
        state.inProcess = false;
        state.warehouse.category = action.payload.category;
        state.updatedAt = Date.now();
      },
      prepare(category: IWarehouseCategory | null) {
        return { payload: { category } };
      },
    },
    downloaderUnpairWarehouseCategory: {
      reducer(state) {
        state.inProcess = true;
        state.warehouse.category = undefined;
      },
      prepare(uuid: string) {
        return { payload: { warehouseCategory: { uuid } } };
      },
    },
    downloaderUnpairWarehouseCategoryResponse: {
      reducer(
        state,
        action: { payload: { category: IWarehouseCategory | null } },
      ) {
        state.inProcess = false;
        state.warehouse.category = action.payload.category;
        state.updatedAt = Date.now();
      },
      prepare(category: IWarehouseCategory | null) {
        return { payload: { category } };
      },
    },
    downloaderLoadWarehouseProducts: {
      reducer(state) {
        state.inProcess = true;
        state.warehouse.products = undefined;
      },
      prepare(
        warehouseUuid: string,
        minimalWeight: number,
        maximalWeight: number,
        minimalPrice: number,
        maximalPrice: number,
      ) {
        return {
          payload: {
            warehouse: { uuid: warehouseUuid },
            weight: { minimal: minimalWeight, maximal: maximalWeight },
            price: { minimal: minimalPrice, maximal: maximalPrice },
          },
        };
      },
    },
    downloaderLoadWarehouseProductsResponse: {
      reducer(
        state,
        action: { payload: { products: IAdminWarehouseProductList[] } },
      ) {
        state.inProcess = false;
        state.warehouse.products = action.payload.products;
        state.updatedAt = Date.now();
      },
      prepare(products: IAdminWarehouseProductList[]) {
        return { payload: { products } };
      },
    },
    downloaderLoadWarehouseProduct: {
      reducer(state) {
        state.inProcess = true;
        state.warehouse.product = undefined;
      },
      prepare(uuid: string) {
        return { payload: { warehouseProduct: { uuid } } };
      },
    },
    downloaderLoadWarehouseProductResponse: {
      reducer(
        state,
        action: { payload: { product: IAdminWarehouseProductDetail | null } },
      ) {
        state.inProcess = false;
        state.warehouse.product = action.payload.product;
        state.updatedAt = Date.now();
      },
      prepare(product: IAdminWarehouseProductDetail | null) {
        return { payload: { product } };
      },
    },
    downloaderWarehouseProductSuggestionsLoad: {
      reducer(state) {
        state.warehouse.suggestions = undefined;
      },
      prepare(warehouseProductUUID: string) {
        return {
          payload: { warehouseProduct: { uuid: warehouseProductUUID } },
        };
      },
    },
    downloaderWarehouseProductSuggestionsLoadResponse: {
      reducer(
        state,
        action: {
          payload: { suggestions: IWarehouseProductSuggestions | null };
        },
      ) {
        state.warehouse.suggestions = action.payload.suggestions;
      },
      prepare(suggestions: IWarehouseProductSuggestions | null) {
        return { payload: { suggestions } };
      },
    },
    downloaderLoadWarehouseProductsByWarehouseCategory: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(uuid: string) {
        return { payload: { warehouseCategory: { uuid } } };
      },
    },
    downloaderLoadWarehouseProductsByWarehouseCategoryResponse: {
      reducer(
        state,
        action: { payload: { products: IAdminWarehouseProductList[] | null } },
      ) {
        state.inProcess = false;
        state.warehouse.products = action.payload.products;
        state.updatedAt = Date.now();
      },
      prepare(products: IAdminWarehouseProductList[] | null) {
        return { payload: { products } };
      },
    },
    downloaderAddWarehouseProduct: {
      reducer(state) {
        state.inProcess = true;
        state.newProduct = undefined;
      },
      prepare(
        downloaderWarehouseProductUUID: string,
        name: string,
        overrideCategoryUUIDs?: string[],
      ) {
        return {
          payload: {
            warehouseProduct: { uuid: downloaderWarehouseProductUUID },
            name,
            overrideCategoryUUIDs,
          },
        };
      },
    },
    downloaderAddWarehouseProductAI: {
      reducer(state) {
        state.inProcess = true;
        state.newProduct = undefined;
      },
      prepare(
        downloaderWarehouseProductUUID: string,
        name: string,
        overrideCategoryUUIDs?: string[],
      ) {
        return {
          payload: {
            warehouseProduct: { uuid: downloaderWarehouseProductUUID },
            name,
            overrideCategoryUUIDs,
          },
        };
      },
    },
    downloaderAddWarehouseProductAILocal: {
      reducer(state) {
        state.inProcess = true;
        state.newProduct = undefined;
      },
      prepare(
        downloaderWarehouseProductUUID: string,
        name: string,
        overrideCategoryUUIDs?: string[],
      ) {
        return {
          payload: {
            warehouseProduct: { uuid: downloaderWarehouseProductUUID },
            name,
            overrideCategoryUUIDs,
          },
        };
      },
    },
    downloaderAddWarehouseProductResponse: {
      reducer(state, action: { payload: { product: IAdminProduct | null } }) {
        state.inProcess = false;
        state.newProduct = action.payload.product;
        state.updatedAt = Date.now();
      },
      prepare(product: IAdminProduct | null) {
        return { payload: { product } };
      },
    },
    downloaderSyncWarehouseProduct: {
      reducer() {},
      prepare(productUUID: string | undefined, warehouseProductUUID: string) {
        return {
          payload: {
            product: { uuid: productUUID },
            warehouseProduct: { uuid: warehouseProductUUID },
          },
        };
      },
    },
    downloaderSyncWarehouseProductResponse: {
      reducer() {},
      prepare(synced: boolean) {
        return { payload: { synced } };
      },
    },
    downloaderUnassignWarehouseProduct: {
      reducer() {},
      prepare(
        productUUID: string,
        warehouseUUID: string,
        warehouseProductUUID?: string,
      ) {
        return {
          payload: {
            product: { uuid: productUUID },
            warehouseProduct: { uuid: warehouseProductUUID },
            warehouse: { uuid: warehouseUUID },
          },
        };
      },
    },
    downloaderUnassignWarehouseProductResponse: {
      reducer() {},
      prepare(unassigned: boolean) {
        return { payload: { unassigned } };
      },
    },
    downloaderDownloadAllPhotosWarehouseProduct: {
      reducer() {},
      prepare(uuid: string) {
        return { payload: { warehouseProduct: { uuid } } };
      },
    },
    downloaderDownloadAllPhotosWarehouseProductResponse: {
      reducer() {},
      prepare(downloaded: boolean) {
        return { payload: { downloaded } };
      },
    },
    downloaderWarehouseProductPairWithExistingProduct: {
      reducer() {},
      prepare(productUUID: string, warehouseProductUUID: string) {
        return {
          payload: {
            product: { uuid: productUUID },
            warehouseProduct: { uuid: warehouseProductUUID },
          },
        };
      },
    },
    downloaderWarehouseProductPairWithExistingProductResponse: {
      reducer() {},
      prepare(paired: boolean) {
        return { payload: { paired } };
      },
    },
    downloaderLoadWarehouseProperties: {
      reducer(state) {
        state.inProcess = true;
        state.warehouse.properties = undefined;
      },
      prepare(assignedToProperty: boolean | undefined = undefined) {
        return { payload: { assignedToProperty } };
      },
    },
    downloaderLoadWarehousePropertiesResponse: {
      reducer(
        state,
        action: { payload: { properties: IWarehouseProperty[] } },
      ) {
        state.inProcess = false;
        state.warehouse.properties = action.payload.properties;
        state.updatedAt = Date.now();
      },
      prepare(properties: IWarehouseProperty[]) {
        return { payload: { properties } };
      },
    },
    downloaderLoadWarehouseProperty: {
      reducer(state) {
        state.inProcess = true;
        state.property = undefined;
      },
      prepare(rawName: string) {
        return { payload: { warehouseProperty: { rawName } } };
      },
    },
    downloaderLoadWarehousePropertyResponse: {
      reducer(
        state,
        action: { payload: { property: IWarehouseProperty | null } },
      ) {
        state.inProcess = false;
        state.property = action.payload.property;
        state.updatedAt = Date.now();

        if (action.payload.property?.values && state.property?.values) {
          state.property.values = action.payload.property.values.sort(
            (a: string, b: string): number => stringCompare(a, b),
          );
        }
      },
      prepare(property: IWarehouseProperty | null) {
        return { payload: { property } };
      },
    },
    downloaderDeleteWarehouseProduct: {
      reducer() {},
      prepare(uuid: string) {
        return { payload: { warehouseProduct: { uuid } } };
      },
    },
    downloaderDeleteWarehouseProductResponse: {
      reducer() {},
      prepare(deleted: boolean) {
        return { payload: { deleted } };
      },
    },
    downloaderHideWarehouseProduct: {
      reducer() {},
      prepare(uuid: string, hide: boolean) {
        return { payload: { warehouseProduct: { uuid }, hide } };
      },
    },
    downloaderHideWarehouseProductResponse: {
      reducer() {},
      prepare(hidden: boolean) {
        return { payload: { hidden } };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      requestFailedMatcher('Downloader'),
      resetLoadingOnRequestFailed,
    );
  },
});

export const {
  downloaderAddWarehouseProduct,
  downloaderAddWarehouseProductAI,
  downloaderAddWarehouseProductAILocal,
  downloaderAddWarehouseProductResponse,
  downloaderDeleteWarehouseProduct,
  downloaderDeleteWarehouseProductResponse,
  downloaderDownloadAllPhotosWarehouseProduct,
  downloaderDownloadAllPhotosWarehouseProductResponse,
  downloaderHideWarehouseProduct,
  downloaderHideWarehouseProductResponse,
  downloaderLoadProduct,
  downloaderLoadProductResponse,
  downloaderLoadProducts,
  downloaderLoadProductsRespose,
  downloaderLoadWarehouseCategories,
  downloaderLoadWarehouseCategoriesResponse,
  downloaderLoadWarehouseCategoriesUnpaired,
  downloaderLoadWarehouseCategoriesUnpairedResponse,
  downloaderLoadWarehouseCategory,
  downloaderLoadWarehouseCategoryResponse,
  downloaderLoadWarehouseProduct,
  downloaderLoadWarehouseProductResponse,
  downloaderLoadWarehouseProducts,
  downloaderLoadWarehouseProductsByWarehouseCategory,
  downloaderLoadWarehouseProductsByWarehouseCategoryResponse,
  downloaderLoadWarehouseProductsResponse,
  downloaderLoadWarehouseProperties,
  downloaderLoadWarehousePropertiesResponse,
  downloaderLoadWarehouseProperty,
  downloaderLoadWarehousePropertyResponse,
  downloaderPairWarehouseCategory,
  downloaderPairWarehouseCategoryResponse,
  downloaderSyncWarehouseProduct,
  downloaderSyncWarehouseProductResponse,
  downloaderUnassignWarehouseProduct,
  downloaderUnassignWarehouseProductResponse,
  downloaderUnpairWarehouseCategory,
  downloaderUnpairWarehouseCategoryResponse,
  downloaderWarehouseProductPairWithExistingProduct,
  downloaderWarehouseProductPairWithExistingProductResponse,
  downloaderWarehouseProductSuggestionsLoad,
  downloaderWarehouseProductSuggestionsLoadResponse,
} = downloaderSlice.actions;

export default downloaderSlice.reducer;
